import { useContext, useEffect, useState } from 'react';
import useSmartState from '../../../common/hooks/useSmartState';
import FormBuilder from '../../../library/Builders/FormBuilder';
import { SmartContext } from '../../../library/Core/SmartContext';
import { formatFullName, getDateDDMMYYYY, getDomainValueForCode, getFuzzySearchCriteria, isEmpty } from '../../../library/Core/SmartFunctions';
import { FormSection } from '../../../library/Core/SmartTypes';
import FiltersApplied from '../../../library/SimpleControls/FiltersApplied';
import InternalAdminReportService from './InternalAdminReportService';
import ExportToExcelCommon from '../../../library/SimpleControls/ExportToExcelCommon';
import useInternalAdminReports from './useInternalAdminReports';

export const SearchCriteria = () => {
    const { state, dispatch } = useContext(SmartContext);
    const { setStateValue } = useSmartState();
    const searchFormConfig = state.formConfig?.sectionRepository[0] as FormSection;
    const [isExpanded, setIsExpanded] = useState(true);
    const [fuzzySearchText, setFuzzySearchText] = useState('');
    const [loading, setLoading] = useState(false);
    const fuzzySearchPlaceholder = searchFormConfig?.fuzzySearchPlaceholder || 'Search by Alumni Name, Email, User ID';
    const { getFormattedDate } = useInternalAdminReports();

    useEffect(() => {
        let isActive = true;
        setLoading(true);

        InternalAdminReportService.search(state.data.searchCriteria, state.routeInfo.pageName)
            .then((response: any) => {
                if (!isActive) return;
                setStateValue('alumniList', response.data);
                fetchAlumniCount(state);
            })
            .catch((error: any) => {
                console.error('Search API Error', error);
            })
            .finally(() => {
                if (isActive) setLoading(false);
            });

        return () => {
            isActive = false;
        };
    }, [state.data.searchCriteria]);

    const handleFuzzySearch = (event: any) => {
        event.preventDefault();
        // if (isEmpty(fuzzySearchText)) return;

        const currentSearchCriteria = {...state.data.searchCriteria};

        currentSearchCriteria.grievanceCode  = null;
        currentSearchCriteria.name  = null;

        const mergedSearchCriteria = {
            ...currentSearchCriteria,
            ...getFuzzySearchCriteria(fuzzySearchText) 
        };
        setStateValue('searchCriteria',mergedSearchCriteria);
    };

    const handleClearAll = () => {
        const defaultCriteria = InternalAdminReportService.getDefaultSearchCriteria(state.routeInfo.pageName);

        defaultCriteria.lastCallStatusCode = "";
        defaultCriteria.accountStatusCode = "";
        defaultCriteria.profileCompletionStatusCode = "";
        defaultCriteria.level = [];
        defaultCriteria.isPrestigiousCollege = "";
        defaultCriteria.isPrestigiousUniversity="";
        defaultCriteria.schoolClientStatusCode = "";
        defaultCriteria.testProfileStatusCode = "";
        defaultCriteria.isValidCollegeCode = "";
        defaultCriteria.isValidCourseCode = "";
        defaultCriteria.schoolGroupId = "";
        
        defaultCriteria.pairingWithSimilarAlumni = "";
        defaultCriteria.networkForSocialCausesVolunteer = "";
        defaultCriteria.networkForBusinessIdeasAndPartnerships = "";
        defaultCriteria.networkSamePgCourseAlumni = "";
        defaultCriteria.jobAndInternships = "";
        defaultCriteria.eventsInterestedInUpskilling = "";
        defaultCriteria.eventsInterestedInEntrepreneurship = "";
        defaultCriteria.hasHigherStudies = "";

        defaultCriteria.collegeTypeCodeJson = "";
        defaultCriteria.examTypeCodeJson = "";
        defaultCriteria.presentEducationStatusCode = "";
        defaultCriteria.ispeerReviewed="";
        defaultCriteria.membershipFeePaymentStatusCode=[];
        
        defaultCriteria.grievanceCode="";

        document.querySelectorAll('.css-1dimb5e-singleValue').forEach(element => {
            element.textContent = ''; 
        }); //universityId text not removed after clear

        if(state.routeInfo.pageName === "internal-admin-user-entered-college") defaultCriteria.isNonStandardCollege = 1;
        if(state.routeInfo.pageName === "internal-admin-user-entered-exam") defaultCriteria.isNonStandardExam = 1;
        if(state.routeInfo.pageName === 'internal-admin-profile-marker') {
            defaultCriteria.isprofileMarkerAdded = 1;
            defaultCriteria.userStatusCode = 'ACTIVE';
            defaultCriteria.pendingLoginActivation = [];
            delete defaultCriteria.isRaw;
            delete defaultCriteria.isSemiVerified;
        }
                
        setStateValue('searchCriteria', defaultCriteria);
    };

    const fetchAlumniCount = async (state:any) => {
        try {
            const alumniCountData = await InternalAdminReportService.searchCount(state.data.searchCriteria, state.routeInfo.pageName);
            await new Promise(resolve => setTimeout(resolve, 200));
            dispatch({
                type: 'SET_INTERNAL_STATE',
                payload: { key:"alumniCount", value:alumniCountData?.data },
            });
        } catch (error) {
            console.error("Error fetching alumni count:", error);
        }
    }
    const exportData = (state:any) => {
        let dataToExport = [];
        let fileName = 'excel-export.xlsx';
        switch(state.routeInfo.pageName){
            case 'inst-admin-grievance':
                [dataToExport, fileName] = getGrievanceExportData(state);
                break;
            default:
                break;
        }
        
        ExportToExcelCommon(
            dataToExport,
            fileName,
            'Grievances'
        )
    };
    const getGrievanceExportData = (state:any) => {
        return [
            state.data.alumniList?.map((row:any) => ({
                "Grievance ID": row.grievanceCode,
                "Alumni Name": formatFullName(row.grievanceUserName, ''),
                "Program": row.grievanceUserProgram,
                "Batch Year": row.grievanceUserBatch,
                "Grievance Type": getDomainValueForCode(row.grievanceCategory, "GRIEVANCE_CATEGORY", state),
                "Priority": getDomainValueForCode(row.grievancePriority, "GRIEVANCE_PRIORITY", state),
                "Status": getDomainValueForCode(row.grievanceStatus, "GRIEVANCE_STATUS", state),
                "Created Date": getDateDDMMYYYY(row.createdAtDdMmYyyy),
                "Last Updated Date": getDateDDMMYYYY(row.updatedAtDdMmYyyy),
                "Description": row.description?.replace(/<[^>]*>/g, '') ?? '',
                "Mode of Communication": getDomainValueForCode(row.mode, "GRIEVANCE_COMMUNICATION_MODE", state)
            })),
            `grievance_export_${new Date().toLocaleDateString('en-GB').replace(/\//g, '-')}.xlsx`
        ]
    }

    return (
        <>
            {loading && (
                <div className="loader-container">
                    <div className="text-center">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 mb-0">Applying filters...</p>
                    </div>
                </div>
            )}
            <div className='row justify-content-between align-items-end align-items-sm-center flex-column-reverse flex-sm-row'>
                <div className="col">
                    <div className="d-flex flex-wrap justify-content-between align-items-start align-items-md-center mb-2 filterSearch">
                        <div className="d-flex flex-wrap flex-1 mb-0 justify-content-start">
                            <button className="btn-grpfiter me-3" aria-expanded={isExpanded} onClick={() => setIsExpanded(!isExpanded)}>
                                <i className="icon-filter font-13 me-2"></i>
                                Filters
                            </button>
                            {state?.routeInfo?.pageName != 'internal-admin-all-school-statistics' && (
                            <div className="search alumniSearch m-0 open position-static flex-1 mb-0">
                                <form className="form-inline d-flex flex-wrap justify-content-between d-block" onSubmit={handleFuzzySearch}>
                                    <input
                                        value={fuzzySearchText}
                                        // placeholder="Search by Alumni Name, Email, User ID"
                                        placeholder={fuzzySearchPlaceholder}
                                        aria-label="Search"
                                        className="form-control flex-1 font-13 font-500 text-truncate"
                                        onChange={(event) => setFuzzySearchText(event?.target.value)}
                                    />
                                    <button className="btn btn-search icon icon-search"></button>
                                </form>
                                {/* <a href="" className="btn-search-toggle icon-search"></a> */}
                            </div>
                            )}
                            
                        </div>
                    </div>
                </div>
                {['inst-admin-grievance'].includes(state?.routeInfo?.pageName) && (
                    <div className="col-auto">
                        <button onClick={() => exportData(state)} className="btn btn-skyblue w-auto height-40 d-inline-flex text-white font-14 text-decoration-none aos-init aos-animate px-3 mb-2"> 
                            <i className="bi bi-box-arrow-up-right font-16 me-1"></i>
                            <span>Export</span>
                        </button>
                    </div>
                )}
                
            </div>
            {isExpanded && (
                <div className="fitergroup p-3 bg-gray-f2 mb-3">
                    <FormBuilder key="searchCriteria" section={searchFormConfig} dataKey="searchCriteria" />
                </div>
            )}

            
            

            <div className="filterresult">
                <FiltersApplied dataKey="searchCriteria" onClearAll={handleClearAll} />
            </div>
        </>
    );
};